import React, { useRef } from 'react'

import { IFieldOption } from 'features/assets/model/types/assets'
import { useOnClickTag } from 'features/common/hooks/useOnClickTag'
import {
  selectFoundedAiTagsSelector,
  selectFoundedStatusesSelector,
  selectFoundedTagsSelector,
  selectSearchAiTagsSelector,
  selectSearchIsLoadingSelector,
  selectSearchIsSearchInputActiveSelector,
  selectSearchIsTagMenuVisibleSelector,
  selectSearchStatusSelector,
  selectSearchTagsSelector
} from 'features/common/model/selectors/searchSelectors'
import {
  clearAllTags,
  clearPrevStateSearch,
  setIsSearchInputActive
} from 'features/common/model/slice/searchSlice'
import { getStatusColor } from 'features/common/utils/statusColors'
import { openFilesPreviewThunk } from 'features/files-preview/model/slice/filesPreviewSlice'
import { useNavigate } from 'react-router-dom'
import { SearchNode } from 'shared/api/searchApi'
import { useAppDispatch, useAppSelector } from 'shared/config/store'
import { RightOutlined, Tag } from 'shared/ui'
import { clearSearchParamsFromURL } from 'shared/utils/urlUtils'
import styled from 'styled-components'

import { TagMenu } from './TagMenu'

interface SearchMenuProps {
  inputValue?: string
  labels: any[]
  statuses: any[]
  tags: any[]
  searchAssets: SearchNode[]
  searchBoards: SearchNode[]
  searchAiTags: IFieldOption[]
  isTagsSearchMenuVisible: boolean
  selectTagsRef: React.RefObject<HTMLElement>
  tagMenuRef: React.RefObject<HTMLElement>
}

export const SearchMenu: React.FC<SearchMenuProps> = ({
  inputValue,
  labels,
  statuses,
  tags,
  searchAssets,
  searchBoards,
  selectTagsRef,
  tagMenuRef
}) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const menuContainerRef = useRef<HTMLDivElement>(null)
  const foundedTags = useAppSelector(selectFoundedTagsSelector)
  const foundedAiTags = useAppSelector(selectFoundedAiTagsSelector)
  const foundedStatuses = useAppSelector(selectFoundedStatusesSelector)
  const hasSearchResults = searchAssets?.length > 0 || searchBoards?.length > 0 || foundedAiTags?.length > 0 || foundedTags?.length > 0 || foundedStatuses?.length > 0
  const isLoading = useAppSelector(selectSearchIsLoadingSelector)
  const isSearchInputActive = useAppSelector(selectSearchIsSearchInputActiveSelector)
  const isTagMenuVisible = useAppSelector(selectSearchIsTagMenuVisibleSelector)
  const { handleAiTagClick, handleTagClick, handleStatusClick } = useOnClickTag()
  const searchTags = useAppSelector(selectSearchTagsSelector)
  const searchAiTags = useAppSelector(selectSearchAiTagsSelector)
  const searchStatus = useAppSelector(selectSearchStatusSelector)
  const hasActiveTags = searchTags.length > 0 || searchAiTags.length > 0 || searchStatus.length > 0

  const handleClickBoard = (board: SearchNode) => {
    navigate(`/dashboard/board/${board.id}`)
    dispatch(setIsSearchInputActive(false))
  }


  if (!isSearchInputActive) return null

  if(isLoading &&  searchBoards.length === 0 && searchAssets.length === 0 && foundedAiTags.length === 0 && foundedTags.length === 0 && foundedStatuses.length === 0) return (
    <SSearchMenuWrapper>
      <SSearchMenuContainer>
        <STitle>
        Loading...
        </STitle>
      </SSearchMenuContainer>
    </SSearchMenuWrapper>
  )

  return (
    <SSearchMenuWrapper>
      {inputValue.trim() && foundedStatuses.length === 0 && foundedTags.length === 0 && foundedAiTags.length === 0
      && labels.length === 0 && tags.length === 0 && searchBoards.length === 0
      && searchAssets.length === 0 && !isLoading && !isTagMenuVisible ? (
          <SSearchMenuContainer>
            No results found
          </SSearchMenuContainer>
        ) : (
          <SSearchMenuContainer
            isScrollable={!isTagMenuVisible}
            ref={menuContainerRef}
          >
            {isTagMenuVisible || hasActiveTags ? (
              <TagMenu
                tagMenuRef={tagMenuRef}
                selectTagsRef={selectTagsRef}
                menuContainerRef={menuContainerRef}
              />
            ) : (
              <>
                {searchBoards.length > 0 && (
                  <SSection>
                    <STitle>
Boards
                    </STitle>

                    <SList>
                      {searchBoards.map((board) => (
                        <SRow
                          key={board.id}
                          onClick={() => {
                            dispatch(clearAllTags())
                            dispatch(clearPrevStateSearch())
                            handleClickBoard(board)
                          }}
                        >
                          <SIcon src={'/images/board.svg'} />

                          <SName>
                            {board.name}
                          </SName>

                          <RightOutlined />
                        </SRow>
                      ))}
                    </SList>
                  </SSection>
                )}

                {foundedTags.length > 0 && (
                  <SSection>
                    <STitle>
                    Tags
                    </STitle>

                    <SAiTagsContainer>
                      {foundedTags.map((tag) => (
                        <SAiTag key={tag.id}
                          onClick={() => handleTagClick(tag)}
                        >
                          {tag.value}
                        </SAiTag>
                      ))}
                    </SAiTagsContainer>

                  </SSection>
                )}

                {foundedAiTags.length > 0 && (
                  <SSection>
                    <STitle>
                    AI tags
                    </STitle>

                    <SAiTagsContainer>
                      {foundedAiTags.map((tag) => (
                        <SAiTag
                          key={tag.id}
                          icon={<SAiIcon src={'/icons/ai.svg'} />}
                          onClick={() => handleAiTagClick(tag)}
                        >
                          {tag.name}
                        </SAiTag>
                      ))}
                    </SAiTagsContainer>
                  </SSection>
                )}

                {searchAssets.length > 0 && (
                  <SSection>
                    <STitle>
Assets
                    </STitle>

                    <SList>
                      {searchAssets.map((asset) => (
                        <SRow
                          key={asset.id}
                          onClick={() => {
                            dispatch(clearAllTags())
                            dispatch(clearPrevStateSearch())
                            dispatch(openFilesPreviewThunk({ items: searchAssets as any, currentItemId: asset.id, totalItemsCount: 1, navigate }))
                            dispatch(setIsSearchInputActive(false))
                            clearSearchParamsFromURL()
                          }}
                        >
                          <SIcon src={'/images/asset.svg'} />

                          <SName>
                            {asset.name}
                          </SName>

                          <RightOutlined />
                        </SRow>
                      ))}
                    </SList>
                  </SSection>
                )}

                {foundedStatuses.length > 0 && (
                  <SSection>
                    <STitle>
                    Statuses
                    </STitle>

                    <SStatusesContainer>
                      {foundedStatuses.map((status) => (
                        <SStatus key={status.id}
                          color={getStatusColor(status.value)}
                          onClick={() => handleStatusClick(status)}
                        >
                          {status.value}
                        </SStatus>
                      ))}
                    </SStatusesContainer>

                  </SSection>
                )}

                {!hasSearchResults && tags.length > 0 && !hasActiveTags && (
                  <SSection>
                    <STitle>
             Tags
                    </STitle>

                    <SAiTagsContainer>
                      {tags?.map((tag) => (
                        <SAiTag
                          key={tag.name}
                          onClick={() => handleTagClick(tag)}
                        >
                          {tag.value}
                        </SAiTag>
                      ))}
                    </SAiTagsContainer>
                  </SSection>
                )}

                {!hasSearchResults && labels.length > 0 && !hasActiveTags && (
                  <SSection>
                    <STitle>
AI tags
                    </STitle>

                    <SAiTagsContainer>
                      {labels?.map((label) => (
                        <SAiTag
                          key={label.name}
                          icon={<SAiIcon src={'/icons/ai.svg'} />}
                          onClick={() => handleAiTagClick(label)}
                        >
                          {label.name}
                        </SAiTag>
                      ))}
                    </SAiTagsContainer>
                  </SSection>
                )}

                {!hasSearchResults && statuses.length > 0 && !hasActiveTags && (
                  <SSection>
                    <STitle>
Statuses
                    </STitle>

                    <SStatusesContainer>
                      {statuses?.map((status) => (
                        <SStatus
                          key={status.id}
                          color={getStatusColor(status.value)}
                          onClick={() => handleStatusClick(status)}
                        >
                          {status.value}
                        </SStatus>
                      ))}
                    </SStatusesContainer>
                  </SSection>
                )}
              </>
            )}
          </SSearchMenuContainer>
        )}

    </SSearchMenuWrapper>
  )
}


const SList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const SName = styled.span`
  overflow: hidden;
  width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: rgba(0, 0, 0, 0.88);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
`

const SIcon = styled.img`
`

const SRow = styled.div`  
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`

const SSearchMenuWrapper = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  z-index: 10;
`

const SAiIcon = styled.img`
  width: 12px;
  height: 12px;
`

const SSearchMenuContainer = styled.div<{ isScrollable?: boolean }>`
  margin-left: 10px;
  margin-right: -10px;
  display: flex;
  padding: 13px 18px 14px;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;    
  border-radius: 8px;
  background: #FFF;
  box-shadow: 0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12), 0px 9px 28px 8px rgba(0, 0, 0, 0.05);
  max-height: ${({ isScrollable }) => isScrollable ? '600px' : 'none'};
  overflow-y: ${({ isScrollable }) => isScrollable ? 'auto' : 'unset'};
`

const SSection = styled.div`
  width: 100%;

  &:last-child {
    margin-bottom: 0;
  }
`

const STitle = styled.span`
  display: flex;
  height: 32px;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.65);
`

const SAiTagsContainer = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
`

const SAiTag = styled(Tag)`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-right: 0;
  cursor: pointer;
  border: 0;
  background: rgba(0, 0, 0, 0.06);
  white-space: normal;
  word-break: break-word;    
  max-width: 100%;      
`

const SStatusesContainer = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: hidden;
`

const SStatus = styled(Tag)`
  display: flex;
  align-items: center;
  border-radius: 4px;
  cursor: pointer;
`


